import { IAimProfilePatch, IArcSettingsPatch, IUserDisplaySettings } from '@common/interfaces/user'
import { IApi } from '../../api'
import { createAction } from '../types'

export const prefix = 'profile'

export const PROFILE_REQUEST = `${prefix}/PROFILE_REQUEST`
export const PROFILE_SUCCESS = `${prefix}/PROFILE_SUCCESS`
export const PROFILE_FAILURE = `${prefix}/PROFILE_FAILURE`

export const loadProfile = createAction(() => ({
  type: [PROFILE_REQUEST, PROFILE_SUCCESS, PROFILE_FAILURE],
  promise: (api: IApi): Promise<unknown> => api.auth.profile(),
}))

/** The profile from the offline snapshot, shown until the network one arrives — see offlineSnapshot. */
export const PROFILE_HYDRATED = `${prefix}/PROFILE_HYDRATED`

export const LOGIN_REQUEST = `${prefix}/LOGIN_REQUEST`
export const LOGIN_SUCCESS = `${prefix}/LOGIN_SUCCESS`
export const LOGIN_FAILURE = `${prefix}/LOGIN_FAILURE`

export const login = createAction(({ email, password }: { email: string; password: string }) => ({
  type: [LOGIN_REQUEST, LOGIN_SUCCESS, LOGIN_FAILURE],
  promise: (api: IApi) => api.auth.login(email, password),
}))

// Google authentication actions
export const GOOGLE_AUTH_REQUEST = `${prefix}/GOOGLE_AUTH_REQUEST`
export const GOOGLE_AUTH_SUCCESS = `${prefix}/GOOGLE_AUTH_SUCCESS`
export const GOOGLE_AUTH_FAILURE = `${prefix}/GOOGLE_AUTH_FAILURE`

export const googleAuth = createAction((token: string) => ({
  type: [GOOGLE_AUTH_REQUEST, GOOGLE_AUTH_SUCCESS, GOOGLE_AUTH_FAILURE],
  promise: (api: IApi) => api.auth.googleAuth(token),
}))

export const MICROSOFT_AUTH_REQUEST = `${prefix}/MICROSOFT_AUTH_REQUEST`
export const MICROSOFT_AUTH_SUCCESS = `${prefix}/MICROSOFT_AUTH_SUCCESS`
export const MICROSOFT_AUTH_FAILURE = `${prefix}/MICROSOFT_AUTH_FAILURE`

export const microsoftAuth = createAction((token: string) => ({
  type: [MICROSOFT_AUTH_REQUEST, MICROSOFT_AUTH_SUCCESS, MICROSOFT_AUTH_FAILURE],
  promise: (api: IApi) => api.auth.microsoftAuth(token),
}))

export const LINK_GOOGLE_REQUEST = `${prefix}/LINK_GOOGLE_REQUEST`
export const LINK_GOOGLE_SUCCESS = `${prefix}/LINK_GOOGLE_SUCCESS`
export const LINK_GOOGLE_FAILURE = `${prefix}/LINK_GOOGLE_FAILURE`

export const linkGoogle = createAction((token: string) => ({
  type: [LINK_GOOGLE_REQUEST, LINK_GOOGLE_SUCCESS, LINK_GOOGLE_FAILURE],
  promise: (api: IApi) => api.auth.linkGoogle(token),
}))

export const UNLINK_GOOGLE_REQUEST = `${prefix}/UNLINK_GOOGLE_REQUEST`
export const UNLINK_GOOGLE_SUCCESS = `${prefix}/UNLINK_GOOGLE_SUCCESS`
export const UNLINK_GOOGLE_FAILURE = `${prefix}/UNLINK_GOOGLE_FAILURE`

export const unlinkGoogle = createAction(() => ({
  type: [UNLINK_GOOGLE_REQUEST, UNLINK_GOOGLE_SUCCESS, UNLINK_GOOGLE_FAILURE],
  promise: (api: IApi) => api.auth.unlinkGoogle(),
}))

export const LINK_MICROSOFT_REQUEST = `${prefix}/LINK_MICROSOFT_REQUEST`
export const LINK_MICROSOFT_SUCCESS = `${prefix}/LINK_MICROSOFT_SUCCESS`
export const LINK_MICROSOFT_FAILURE = `${prefix}/LINK_MICROSOFT_FAILURE`

export const linkMicrosoft = createAction((token: string) => ({
  type: [LINK_MICROSOFT_REQUEST, LINK_MICROSOFT_SUCCESS, LINK_MICROSOFT_FAILURE],
  promise: (api: IApi) => api.auth.linkMicrosoft(token),
}))

export const UNLINK_MICROSOFT_REQUEST = `${prefix}/UNLINK_MICROSOFT_REQUEST`
export const UNLINK_MICROSOFT_SUCCESS = `${prefix}/UNLINK_MICROSOFT_SUCCESS`
export const UNLINK_MICROSOFT_FAILURE = `${prefix}/UNLINK_MICROSOFT_FAILURE`

export const unlinkMicrosoft = createAction(() => ({
  type: [UNLINK_MICROSOFT_REQUEST, UNLINK_MICROSOFT_SUCCESS, UNLINK_MICROSOFT_FAILURE],
  promise: (api: IApi) => api.auth.unlinkMicrosoft(),
}))

export const LOGOUT_REQUEST = `${prefix}/LOGOUT_REQUEST`
export const LOGOUT_SUCCESS = `${prefix}/LOGOUT_SUCCESS`
export const LOGOUT_FAILURE = `${prefix}/LOGOUT_FAILURE`

/**
 * A deliberate sign-out: the user asked to leave.
 *
 * This is the only path that revokes anything server-side. Revocation writes a Redis
 * denylist keyed on the token *and* on its `sid`, so every re-issue of the session stops
 * verifying — a JWT cannot be un-signed, so refusing it on the way in is the only way to end
 * a session before its seven days are up. That is what makes signing out on a shared machine
 * mean something, and it is worth keeping here.
 *
 * It is worth nothing on an *auth error*, which is why that path dispatches `sessionExpired`
 * instead. See the note there.
 */
export const logout = createAction(() => ({
  type: [LOGOUT_REQUEST, LOGOUT_SUCCESS, LOGOUT_FAILURE],
  promise: (api: IApi) => api.auth.logout(window.localStorage.getItem('token')),
}))

export const SESSION_EXPIRED = `${prefix}/SESSION_EXPIRED`

/**
 * The server refused our token, so drop to the login screen — without telling the server.
 *
 * Reactive sign-out has to be a *different* action from `logout`, because `logout` revokes
 * `window.localStorage`'s current token and on this path that is reliably the wrong one.
 * Sessions slide (server: services/auth/sessionRotation), so a session holds several valid
 * tokens at once and storage holds the newest. The request that just 401'd was carrying an
 * older one — a tab opened before the last rotation, a phone, a reply that outlived the state
 * it set off in. Revoking on its behalf therefore denylists the *healthy* token for seven days
 * and signs the user out of the session that was working.
 *
 * That inverted into a loop in production on 31 Aug: 30 genuine expiries became 196 denylist
 * hits and 166 logouts in one hour, because each spurious logout poisoned the token the next
 * request would carry — and a straggler landing after the user signed back in killed the new
 * session too. Hence: no server call. There is nothing to revoke, either. A 401 means the
 * token is already refused, so revocation buys exactly nothing on top.
 */
export const sessionExpired = () => ({ type: SESSION_EXPIRED })

/**
 * Every action that means "we are no longer signed in", however we got here.
 *
 * The local teardown is identical for both — wipe the store, drop the token, close the socket,
 * go to the login screen — and only the server call differs. Anything keying on sign-out
 * should key on this rather than on `LOGOUT_SUCCESS` alone, or it silently handles half the
 * cases.
 */
export const SIGN_OUT_ACTIONS = [LOGOUT_SUCCESS, SESSION_EXPIRED]

export const CONFIRM_REQUEST = `${prefix}/CONFIRM_REQUEST`
export const CONFIRM_SUCCESS = `${prefix}/CONFIRM_SUCCESS`
export const CONFIRM_FAILURE = `${prefix}/CONFIRM_FAILURE`

export const confirm = createAction((hash: string) => ({
  type: [CONFIRM_REQUEST, CONFIRM_SUCCESS, CONFIRM_FAILURE],
  promise: (api: IApi) => api.auth.confirm(hash),
}))

export const PASSWORD_RECOVERY_REQUEST = `${prefix}/PASSWORD_RECOVERY_REQUEST`
export const PASSWORD_RECOVERY_SUCCESS = `${prefix}/PASSWORD_RECOVERY_SUCCESS`
export const PASSWORD_RECOVERY_FAILURE = `${prefix}/PASSWORD_RECOVERY_FAILURE`

export const passwordRecovery = createAction(({ email }: { email: string }) => ({
  type: [PASSWORD_RECOVERY_REQUEST, PASSWORD_RECOVERY_SUCCESS, PASSWORD_RECOVERY_FAILURE],
  promise: (api: IApi) => api.auth.resetPassword(email),
}))

export const RESET_PASSWORD_REQUEST = `${prefix}/RESET_PASSWORD_REQUEST`
export const RESET_PASSWORD_SUCCESS = `${prefix}/RESET_PASSWORD_SUCCESS`
export const RESET_PASSWORD_FAILURE = `${prefix}/RESET_PASSWORD_FAILURE`

export const resetPassword = createAction(
  ({
    hash,
    password,
    confirmPassword,
  }: {
    hash: string
    password: string
    confirmPassword: string
  }) => ({
    type: [RESET_PASSWORD_REQUEST, RESET_PASSWORD_SUCCESS, RESET_PASSWORD_FAILURE],
    promise: (api: IApi) => api.auth.setPassword(hash, password, confirmPassword),
  }),
)

export const UPDATE_PASSWORD_REQUEST = `${prefix}/UPDATE_PASSWORD_REQUEST`
export const UPDATE_PASSWORD_SUCCESS = `${prefix}/UPDATE_PASSWORD_SUCCESS`
export const UPDATE_PASSWORD_FAILURE = `${prefix}/UPDATE_PASSWORD_FAILURE`

export const updatePassword = createAction(
  ({ password, newPassword }: { password?: string; newPassword: string }) => ({
    type: [UPDATE_PASSWORD_REQUEST, UPDATE_PASSWORD_SUCCESS, UPDATE_PASSWORD_FAILURE],
    promise: (api: IApi) => api.auth.updatePassword(password, newPassword),
  }),
)

export const UPDATE_PROFILE_REQUEST = `${prefix}/UPDATE_PROFILE_REQUEST`
export const UPDATE_PROFILE_SUCCESS = `${prefix}/UPDATE_PROFILE_SUCCESS`
export const UPDATE_PROFILE_FAILURE = `${prefix}/UPDATE_PROFILE_FAILURE`

export const updateProfile = createAction((data: object) => ({
  type: [UPDATE_PROFILE_REQUEST, UPDATE_PROFILE_SUCCESS, UPDATE_PROFILE_FAILURE],
  promise: (api: IApi) => api.auth.updateProfile(data),
}))

export const PROCESS_INVITATION_REQUEST = `dontShow/PROCESS_INVITATION_REQUEST`
export const PROCESS_INVITATION_SUCCESS = `dontShow/PROCESS_INVITATION_SUCCESS`
export const PROCESS_INVITATION_FAILURE = `dontShow/PROCESS_INVITATION_FAILURE`

export const processInvitation = createAction((hash: string) => ({
  type: [PROCESS_INVITATION_REQUEST, PROCESS_INVITATION_SUCCESS, PROCESS_INVITATION_FAILURE],
  promise: (api: IApi) => api.auth.processInvitation(hash),
}))

export const CHECK_SETUP_PASSWORD_REQUEST = `dontShow/CHECK_SETUP_PASSWORD_REQUEST`
export const CHECK_SETUP_PASSWORD_SUCCESS = `dontShow/CHECK_SETUP_PASSWORD_SUCCESS`
export const CHECK_SETUP_PASSWORD_FAILURE = `dontShow/CHECK_SETUP_PASSWORD_FAILURE`

export const checkPasswordSetup = createAction((hash: string) => ({
  type: [CHECK_SETUP_PASSWORD_REQUEST, CHECK_SETUP_PASSWORD_SUCCESS, CHECK_SETUP_PASSWORD_FAILURE],
  promise: (api: IApi) => api.auth.checkPasswordSetup(hash),
}))

export const SETUP_PASSWORD_REQUEST = `${prefix}/SETUP_PASSWORD_REQUEST`
export const SETUP_PASSWORD_SUCCESS = `${prefix}/SETUP_PASSWORD_SUCCESS`
export const SETUP_PASSWORD_FAILURE = `${prefix}/SETUP_PASSWORD_FAILURE`

export const setupPassword = createAction(
  (
    hash: string,
    {
      firstName,
      lastName,
      email,
      phone,
      password,
      confirmPassword,
    }: {
      firstName: string
      lastName: string
      email: string
      phone: string
      password: string
      confirmPassword: string
    },
  ) => ({
    type: [SETUP_PASSWORD_REQUEST, SETUP_PASSWORD_SUCCESS, SETUP_PASSWORD_FAILURE],
    promise: (api: IApi) =>
      api.auth.setupPassword(hash, {
        firstName,
        lastName,
        email,
        phone,
        password,
        confirmPassword,
      }),
  }),
)

export const GOOGLE_INVITATION_AUTH_REQUEST = `${prefix}/GOOGLE_INVITATION_AUTH_REQUEST`
export const GOOGLE_INVITATION_AUTH_SUCCESS = `${prefix}/GOOGLE_INVITATION_AUTH_SUCCESS`
export const GOOGLE_INVITATION_AUTH_FAILURE = `${prefix}/GOOGLE_INVITATION_AUTH_FAILURE`

export const googleInvitationAuth = createAction(
  (
    hash: string,
    {
      token,
    }: {
      token: string
    },
  ) => ({
    type: [
      GOOGLE_INVITATION_AUTH_REQUEST,
      GOOGLE_INVITATION_AUTH_SUCCESS,
      GOOGLE_INVITATION_AUTH_FAILURE,
    ],
    promise: (api: IApi) => api.auth.googleInvitationAuth(hash, { token }),
  }),
)

export const MICROSOFT_INVITATION_AUTH_REQUEST = `${prefix}/MICROSOFT_INVITATION_AUTH_REQUEST`
export const MICROSOFT_INVITATION_AUTH_SUCCESS = `${prefix}/MICROSOFT_INVITATION_AUTH_SUCCESS`
export const MICROSOFT_INVITATION_AUTH_FAILURE = `${prefix}/MICROSOFT_INVITATION_AUTH_FAILURE`

export const microsoftInvitationAuth = createAction(
  (
    hash: string,
    {
      token,
      firstName,
      lastName,
      phone,
    }: {
      token: string
      firstName: string
      lastName: string
      phone?: string
    },
  ) => ({
    type: [
      MICROSOFT_INVITATION_AUTH_REQUEST,
      MICROSOFT_INVITATION_AUTH_SUCCESS,
      MICROSOFT_INVITATION_AUTH_FAILURE,
    ],
    promise: (api: IApi) =>
      api.auth.microsoftInvitationAuth(hash, {
        token,
        firstName,
        lastName,
        phone,
      }),
  }),
)

export const CLIENT_INFO_REQUEST = `${prefix}/CLIENT_INFO_REQUEST`
export const CLIENT_INFO_SUCCESS = `${prefix}/CLIENT_INFO_SUCCESS`
export const CLIENT_INFO_FAILURE = `${prefix}/CLIENT_INFO_FAILURE`

export const loadClientInfo = createAction(() => ({
  type: [CLIENT_INFO_REQUEST, CLIENT_INFO_SUCCESS, CLIENT_INFO_FAILURE],
  promise: (api: IApi) => api.clientInfo.clientInfo(),
}))

export const CLIENT_INFO_RELOAD_REQUEST = `${prefix}/CLIENT_INFO_RELOAD_REQUEST`
export const CLIENT_INFO_RELOAD_SUCCESS = `${prefix}/CLIENT_INFO_RELOAD_SUCCESS`
export const CLIENT_INFO_RELOAD_FAILURE = `${prefix}/CLIENT_INFO_RELOAD_FAILURE`

export const reloadClientInfo = createAction(() => ({
  type: [CLIENT_INFO_RELOAD_REQUEST, CLIENT_INFO_RELOAD_SUCCESS, CLIENT_INFO_RELOAD_FAILURE],
  promise: (api: IApi) => api.clientInfo.clientInfo(),
}))

export const PERMISSION_DENIED = `${prefix}/PERMISSION_DENIED`

export const permissionDenied = () => ({
  type: PERMISSION_DENIED,
})

// The nav rail's pinned state, remembered per browser; read as the reducer's initial state.
export const SIDEBAR_PINNED_STORAGE_KEY = 'atlas.sidebar.pinned'

export const TOGGLE_SIDEBAR = `${prefix}/TOGGLE_SIDEBAR`

export const toggleSidebar = () => ({
  type: TOGGLE_SIDEBAR,
})

export const OPEN_SIDEBAR = `${prefix}/OPEN_SIDEBAR`

export const openSidebar = () => ({
  type: OPEN_SIDEBAR,
})

export const UPDATE_NOTIFICATION_SETTINGS_REQUEST = `${prefix}/UPDATE_NOTIFICATION_SETTINGS_REQUEST`
export const UPDATE_NOTIFICATION_SETTINGS_SUCCESS = `${prefix}/UPDATE_NOTIFICATION_SETTINGS_SUCCESS`
export const UPDATE_NOTIFICATION_SETTINGS_FAILURE = `${prefix}/UPDATE_NOTIFICATION_SETTINGS_FAILURE`

export const updateNotificationSettings = createAction(
  (data: { firstName: string; lastName: string; email: string }) => ({
    type: [
      UPDATE_NOTIFICATION_SETTINGS_REQUEST,
      UPDATE_NOTIFICATION_SETTINGS_SUCCESS,
      UPDATE_NOTIFICATION_SETTINGS_FAILURE,
    ],
    promise: (api: IApi) => api.auth.updateNotificationSettings(data),
  }),
)

export const UPDATE_DISPLAY_SETTINGS_REQUEST = `${prefix}/UPDATE_DISPLAY_SETTINGS_REQUEST`
export const UPDATE_DISPLAY_SETTINGS_SUCCESS = `${prefix}/UPDATE_DISPLAY_SETTINGS_SUCCESS`
export const UPDATE_DISPLAY_SETTINGS_FAILURE = `${prefix}/UPDATE_DISPLAY_SETTINGS_FAILURE`

export const updateDisplaySettings = createAction((data: Partial<IUserDisplaySettings>) => ({
  type: [
    UPDATE_DISPLAY_SETTINGS_REQUEST,
    UPDATE_DISPLAY_SETTINGS_SUCCESS,
    UPDATE_DISPLAY_SETTINGS_FAILURE,
  ],
  promise: (api: IApi) => api.auth.updateDisplaySettings(data),
}))

export const UPDATE_AIM_PROFILE_REQUEST = `${prefix}/UPDATE_AIM_PROFILE_REQUEST`
export const UPDATE_AIM_PROFILE_SUCCESS = `${prefix}/UPDATE_AIM_PROFILE_SUCCESS`
export const UPDATE_AIM_PROFILE_FAILURE = `${prefix}/UPDATE_AIM_PROFILE_FAILURE`

export const updateAimProfile = createAction((data: IAimProfilePatch) => ({
  type: [UPDATE_AIM_PROFILE_REQUEST, UPDATE_AIM_PROFILE_SUCCESS, UPDATE_AIM_PROFILE_FAILURE],
  promise: (api: IApi) => api.auth.updateAimProfile(data),
}))

export const UPDATE_ARC_SETTINGS_REQUEST = `${prefix}/UPDATE_ARC_SETTINGS_REQUEST`
export const UPDATE_ARC_SETTINGS_SUCCESS = `${prefix}/UPDATE_ARC_SETTINGS_SUCCESS`
export const UPDATE_ARC_SETTINGS_FAILURE = `${prefix}/UPDATE_ARC_SETTINGS_FAILURE`

// Partial: each Arc Settings box passes only the preference it changed.
export const updateArcSettings = createAction((data: IArcSettingsPatch) => ({
  type: [UPDATE_ARC_SETTINGS_REQUEST, UPDATE_ARC_SETTINGS_SUCCESS, UPDATE_ARC_SETTINGS_FAILURE],
  promise: (api: IApi) => api.auth.updateArcSettings(data),
}))

export const READ_VOICE_TONE_REQUEST = `${prefix}/READ_VOICE_TONE_REQUEST`
export const READ_VOICE_TONE_SUCCESS = `${prefix}/READ_VOICE_TONE_SUCCESS`
export const READ_VOICE_TONE_FAILURE = `${prefix}/READ_VOICE_TONE_FAILURE`

export const readVoiceTone = createAction(() => ({
  type: [READ_VOICE_TONE_REQUEST, READ_VOICE_TONE_SUCCESS, READ_VOICE_TONE_FAILURE],
  promise: (api: IApi) => api.auth.readVoiceTone(),
}))

export const UPDATE_VOICE_TONE_REQUEST = `${prefix}/UPDATE_VOICE_TONE_REQUEST`
export const UPDATE_VOICE_TONE_SUCCESS = `${prefix}/UPDATE_VOICE_TONE_SUCCESS`
export const UPDATE_VOICE_TONE_FAILURE = `${prefix}/UPDATE_VOICE_TONE_FAILURE`

export const updateVoiceTone = createAction((data: { aiVoiceTone: string }) => ({
  type: [UPDATE_VOICE_TONE_REQUEST, UPDATE_VOICE_TONE_SUCCESS, UPDATE_VOICE_TONE_FAILURE],
  promise: (api: IApi) => api.auth.updateVoiceTone(data),
}))

export const REGENERATE_VOICE_TONE_REQUEST = `${prefix}/REGENERATE_VOICE_TONE_REQUEST`
export const REGENERATE_VOICE_TONE_SUCCESS = `${prefix}/REGENERATE_VOICE_TONE_SUCCESS`
export const REGENERATE_VOICE_TONE_FAILURE = `${prefix}/REGENERATE_VOICE_TONE_FAILURE`

export const regenerateVoiceTone = createAction(() => ({
  type: [
    REGENERATE_VOICE_TONE_REQUEST,
    REGENERATE_VOICE_TONE_SUCCESS,
    REGENERATE_VOICE_TONE_FAILURE,
  ],
  promise: (api: IApi) => api.auth.regenerateVoiceTone(),
}))

export const LIST_LEARNED_PROFILE_HISTORY_REQUEST = `${prefix}/LIST_LEARNED_PROFILE_HISTORY_REQUEST`
export const LIST_LEARNED_PROFILE_HISTORY_SUCCESS = `${prefix}/LIST_LEARNED_PROFILE_HISTORY_SUCCESS`
export const LIST_LEARNED_PROFILE_HISTORY_FAILURE = `${prefix}/LIST_LEARNED_PROFILE_HISTORY_FAILURE`

export const listLearnedProfileHistory = createAction(() => ({
  type: [
    LIST_LEARNED_PROFILE_HISTORY_REQUEST,
    LIST_LEARNED_PROFILE_HISTORY_SUCCESS,
    LIST_LEARNED_PROFILE_HISTORY_FAILURE,
  ],
  promise: (api: IApi) => api.auth.listLearnedProfileHistory(),
}))

export const REVERT_LEARNED_PROFILE_REQUEST = `${prefix}/REVERT_LEARNED_PROFILE_REQUEST`
export const REVERT_LEARNED_PROFILE_SUCCESS = `${prefix}/REVERT_LEARNED_PROFILE_SUCCESS`
export const REVERT_LEARNED_PROFILE_FAILURE = `${prefix}/REVERT_LEARNED_PROFILE_FAILURE`

export const revertLearnedProfile = createAction((id: string) => ({
  type: [
    REVERT_LEARNED_PROFILE_REQUEST,
    REVERT_LEARNED_PROFILE_SUCCESS,
    REVERT_LEARNED_PROFILE_FAILURE,
  ],
  promise: (api: IApi) => api.auth.revertLearnedProfile(id),
}))

export const LOGIN_AS_CLIENT_REQUEST = `${prefix}/LOGIN_AS_CLIENT_REQUEST`
export const LOGIN_AS_CLIENT_SUCCESS = `${prefix}/LOGIN_AS_CLIENT_SUCCESS`
export const LOGIN_AS_CLIENT_FAILURE = `${prefix}/LOGIN_AS_CLIENT_FAILURE`

export const loginAsClient = createAction((clientId: string) => ({
  type: [LOGIN_AS_CLIENT_REQUEST, LOGIN_AS_CLIENT_SUCCESS, LOGIN_AS_CLIENT_FAILURE],
  promise: (api: IApi) => api.auth.loginAsClient(clientId),
}))

export const LOGOUT_AS_CLIENT_REQUEST = `${prefix}/LOGOUT_AS_CLIENT_REQUEST`
export const LOGOUT_AS_CLIENT_SUCCESS = `${prefix}/LOGOUT_AS_CLIENT_SUCCESS`
export const LOGOUT_AS_CLIENT_FAILURE = `${prefix}/LOGOUT_AS_CLIENT_FAILURE`

export const logoutAsClient = createAction(() => ({
  type: [LOGOUT_AS_CLIENT_REQUEST, LOGOUT_AS_CLIENT_SUCCESS, LOGOUT_AS_CLIENT_FAILURE],
  promise: (api: IApi) => api.auth.logoutAsClient(),
}))

export const SWITCH_CLIENT_REQUEST = `${prefix}/SWITCH_CLIENT_REQUEST`
export const SWITCH_CLIENT_SUCCESS = `${prefix}/SWITCH_CLIENT_SUCCESS`
export const SWITCH_CLIENT_FAILURE = `${prefix}/SWITCH_CLIENT_FAILURE`

export const switchClient = createAction((clientId: string) => ({
  type: [SWITCH_CLIENT_REQUEST, SWITCH_CLIENT_SUCCESS, SWITCH_CLIENT_FAILURE],
  promise: (api: IApi) => api.auth.switchClient(clientId),
}))

export const CLEAR_TWO_FACTOR_SUCCESS = `${prefix}/CLEAR_TWO_FACTOR_SUCCESS`

export const clearTwoFactorSuccess = () => ({
  type: CLEAR_TWO_FACTOR_SUCCESS,
})
